import { Button, Group, Modal, Stack, Text, toast } from '@common/ui'
import { useEffect, useState } from 'react'
import { com } from '@eidu/web'
import EntityFieldInputGroup from '../../pages/entity/kinds/shared/EntityFieldInputGroup'
import DraftFieldValue from '../../domain/entity/DraftFieldValue'
import EqualityHashMap from '../../util/EqualityHashMap'
import createEntityToCreate from '../../pages/entity/createEntityToCreate'
import hasFieldError from '../../domain/entity/hasFieldError'
import { useCreateEntity } from '../../io/useEntityMutations'
import FieldId = com.eidu.entity.field.FieldId
import EntityId = com.eidu.entity.EntityId
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId

export type CreateEntityInSetModalProps = {
  opened: boolean
  onClose: () => void
  entityType: EntityType
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  setName: string
  onCreated: (id: EntityId) => Promise<void>
}

const CreateEntityInSetModal = ({
  opened,
  onClose,
  entityType,
  entityTypes,
  setName,
  onCreated,
}: CreateEntityInSetModalProps) => {
  const createMutation = useCreateEntity()
  const [fields, setFields] = useState<ReadonlyMap<FieldId, DraftFieldValue>>(new EqualityHashMap())

  useEffect(() => {
    if (opened) setFields(new EqualityHashMap())
  }, [opened])

  const updateField = (value: DraftFieldValue) => setFields((it) => new EqualityHashMap(it).set(value.fieldId, value))

  const valid = entityType.fields
    .asJsReadonlyArrayView()
    .every((field) => !hasFieldError(field, fields.get(field.id), new Map()))

  const submit = async () => {
    try {
      const newEntityId = await createMutation.mutateAsync(
        createEntityToCreate(entityType.id, [...fields.values()], [], new Map())
      )
      await onCreated(newEntityId)
      toast.positive(`Created ${entityType.name} and added it to ${setName}`)
      onClose()
    } catch (e) {
      console.error(e)
      toast.negative(`Failed to create ${entityType.name}`)
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={`Create ${entityType.name}`} size="sm">
      <Stack gap="md">
        <EntityFieldInputGroup
          entityType={entityType}
          entityTypes={entityTypes}
          fields={fields}
          updateField={updateField}
          loading={false}
        />
        <Text size="sm" c="dimmed">
          This creates a new {entityType.name} entity and adds it to {setName} in one step — same as creating the entity
          elsewhere, then adding it here.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button disabled={!valid || createMutation.isPending} loading={createMutation.isPending} onClick={submit}>
            Create and add
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export default CreateEntityInSetModal
